import { supabase } from '../../../lib/supabaseClient';

export interface WorkflowRoleRecord {
  id: string;
  organizationId: string;
  roleKey: string;
  name: string;
}

export interface UserWorkflowRoleMembership extends WorkflowRoleRecord {
  userId: string;
}

function normalizeWhitespace(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

export function normalizeWorkflowRoleKey(value?: string | null): string | null {
  if (!value) return null;
  const normalized = normalizeWhitespace(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();

  return normalized.length > 0 ? normalized : null;
}

export function humanizeWorkflowRoleName(value?: string | null): string | null {
  const normalized = normalizeWorkflowRoleKey(value);
  if (!normalized) return null;
  return normalized
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * Busca ou cria um papel na tabela workflow_roles_v9.
 * Retorna o registro canônico com UUID real (não mais IDs fake legacy:xxx).
 */
export async function ensureWorkflowRole(
  organizationId: string,
  roleLike?: string | null,
): Promise<WorkflowRoleRecord | null> {
  const roleKey = normalizeWorkflowRoleKey(roleLike);
  if (!organizationId || !roleKey) return null;

  // Tentar buscar existente
  const { data: existing, error: selectErr } = await supabase
    .from('workflow_roles_v9')
    .select('id, organization_id, role_key, name')
    .eq('organization_id', organizationId)
    .eq('role_key', roleKey)
    .maybeSingle();

  if (selectErr) throw selectErr;

  if (existing) {
    return {
      id: existing.id,
      organizationId: existing.organization_id,
      roleKey: existing.role_key,
      name: existing.name,
    };
  }

  // Criar se não existe
  const displayName = humanizeWorkflowRoleName(roleLike) ?? roleKey;
  const { data: inserted, error: insertErr } = await supabase
    .from('workflow_roles_v9')
    .insert({
      organization_id: organizationId,
      role_key: roleKey,
      name: displayName,
      is_system: false,
    })
    .select('id, organization_id, role_key, name')
    .single();

  if (insertErr) {
    // Concurrency: another request may have inserted — retry select
    if (insertErr.code === '23505') {
      const { data: retry } = await supabase
        .from('workflow_roles_v9')
        .select('id, organization_id, role_key, name')
        .eq('organization_id', organizationId)
        .eq('role_key', roleKey)
        .maybeSingle();
      if (retry) {
        return {
          id: retry.id,
          organizationId: retry.organization_id,
          roleKey: retry.role_key,
          name: retry.name,
        };
      }
    }
    throw insertErr;
  }

  return {
    id: inserted.id,
    organizationId: inserted.organization_id,
    roleKey: inserted.role_key,
    name: inserted.name,
  };
}

export async function ensureWorkflowRoles(
  organizationId: string,
  roles: Array<string | null | undefined>,
): Promise<Map<string, WorkflowRoleRecord>> {
  const records = new Map<string, WorkflowRoleRecord>();

  for (const roleLike of roles) {
    const roleKey = normalizeWorkflowRoleKey(roleLike);
    if (!roleKey || records.has(roleKey)) continue;
    const record = await ensureWorkflowRole(organizationId, roleLike);
    if (record) records.set(roleKey, record);
  }

  return records;
}

/**
 * Lista os papéis do usuário consultando user_roles JOIN workflow_roles_v9.
 * Fallback: se não houver registros em user_roles, tenta user_organizations.role
 * e sincroniza para user_roles automaticamente.
 */
export async function listUserWorkflowRoles(
  userId: string,
  organizationId: string,
): Promise<UserWorkflowRoleMembership[]> {
  if (!userId || !organizationId) return [];

  const memberships = new Map<string, UserWorkflowRoleMembership>();

  // Fonte primária: user_roles JOIN workflow_roles_v9
  const { data: modernRoles, error: modernErr } = await supabase
    .from('user_roles')
    .select('role_id, workflow_roles_v9:role_id(id, organization_id, role_key, name)')
    .eq('organization_id', organizationId)
    .eq('user_id', userId);

  if (modernErr) throw modernErr;

  if (modernRoles && modernRoles.length > 0) {
    for (const row of modernRoles) {
      const role = row.workflow_roles_v9 as any;
      if (!role) continue;
      const record: UserWorkflowRoleMembership = {
        id: role.id,
        organizationId: role.organization_id,
        roleKey: role.role_key,
        name: role.name,
        userId,
      };
      memberships.set(record.id, record);
    }
    return Array.from(memberships.values());
  }

  // Fallback: ler user_organizations.role e sincronizar para user_roles
  const { data: legacyRow, error: legacyErr } = await supabase
    .from('user_organizations')
    .select('role')
    .eq('organization_id', organizationId)
    .eq('user_id', userId)
    .maybeSingle();

  if (legacyErr) throw legacyErr;

  const legacyRole = legacyRow?.role as string | undefined;
  if (legacyRole) {
    const roleRecord = await ensureWorkflowRole(organizationId, legacyRole);
    if (roleRecord) {
      // Sincronizar para user_roles (fire-and-forget, ignora conflito)
      supabase
        .from('user_roles')
        .insert({
          organization_id: organizationId,
          user_id: userId,
          role_id: roleRecord.id,
        })
        .then(() => {});

      memberships.set(roleRecord.id, { ...roleRecord, userId });
    }
  }

  return Array.from(memberships.values());
}

/**
 * Verifica se o usuário possui um papel específico.
 * Aceita roleId (UUID) ou roleLike (texto para normalizar).
 */
export async function userHasWorkflowRole(
  userId: string,
  organizationId: string,
  roleId?: string | null,
  roleLike?: string | null,
): Promise<boolean> {
  const memberships = await listUserWorkflowRoles(userId, organizationId);
  const normalizedRoleKey = normalizeWorkflowRoleKey(roleLike);

  return memberships.some((membership) =>
    (roleId ? membership.id === roleId : false) ||
    (normalizedRoleKey ? membership.roleKey === normalizedRoleKey : false),
  );
}

/**
 * Retorna os IDs de role (UUIDs) que o usuário possui na organização.
 */
export async function listUserRoleIds(
  userId: string,
  organizationId: string,
): Promise<string[]> {
  const memberships = await listUserWorkflowRoles(userId, organizationId);
  return memberships.map((m) => m.id);
}
